import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RecurringCard } from '../RecurringItem.style';
import { Alert, debounce } from '@mui/material';
import { buildExpressQuery } from '../../../../../../repository/queries/builders';
import axios from 'axios';
import Table from '../../../../../Table/Table';
import SchedulingContext from '../../../../SchedulingContext';
import moment from 'moment-timezone';
import LocalizationContext from '../../../../../../localization/LocalizationContext';
import useRecurringItemContext from '../useRecurringItemContext';

const formatColumns = (has24HourClock, poNumberLocalization) => {
    const format = has24HourClock ? 'ddd, MM/DD/YYYY HH:mm' : 'ddd, MM/DD/YYYY hh:mm A';
    return [
        { field: 'id', headerName: 'ID', width: 70, hide: true },
        { field: 'po_number', headerName: poNumberLocalization ?? 'PO Number', width: 300 },
        {
            field: 'start_date',
            headerName: 'Start Date',
            width: 300,
            valueFormatter: (params) => moment(params.value).format(format),
        },
        {
            field: 'end_date', // wrap
            headerName: 'End Date',
            width: 300,
            valueFormatter: (params) => moment(params.value).format(format),
        },
    ];
};

const ErrorAlert = ({ tooManyItemsError }) => {
    if (tooManyItemsError) {
        return (
            <Alert
                severity="error"
                sx={(theme) => ({
                    marginY: theme.spacing(2),
                })}
            >
                Too many items being created at once, if you need more than 8000 items please make them in multiple requests.
            </Alert>
        );
    }

    return (
        <Alert
            severity="error"
            sx={(theme) => ({
                marginY: theme.spacing(2),
            })}
        >
            Preview not available for custom week or month items.
        </Alert>
    );
};

function PreviewTableCard({ item }) {
    if (!item) return null;

    const { properties } = useContext(SchedulingContext);
    const localization = useContext(LocalizationContext);
    const { tooManyItemsError, setTooManyItemsError, setIsRecurringItemWarningBlockingCreate, setRecurringAmount } = useRecurringItemContext();

    const [preview, setPreview] = useState([]);

    const getPreview = useCallback(
        debounce(async (i) => {
            const { startDate, endDate, unit } = i;
            if (!startDate || !endDate || !unit) return;
            const query = buildExpressQuery('/schedule-supply-item/recurring-preview', {
                body: {
                    start_date: i.startDate.format(),
                    end_date: i.endDate.format(),
                    po_number: i.po_number,
                    recurring: {
                        frequency: i.frequency === 0 ? i.customFrequency : i.frequency,
                        unit: i.unit,
                        ...(i.days && { days: i.days }),
                        ...(i.recurEndDate && { recur_end_date: i.recurEndDate }),
                        ...(i.recurDayOfMonth && { recur_day_of_month: i.recurDayOfMonth }),
                        ...(i.numberOccurrences && { number_occurrences: i.numberOccurrences }),
                        ...(i.poNumberParams && { po_number_params: i.poNumberParams }),
                    },
                },
            });
            try {
                const items = await axios.post(query.url, query.body);
                setTooManyItemsError(false);
                setPreview(items.data);
                setIsRecurringItemWarningBlockingCreate(items.data.length > 100);
                setRecurringAmount(items?.data?.length);
            } catch (error) {
                setPreview([]);
                setTooManyItemsError(true);
                setIsRecurringItemWarningBlockingCreate(false);
                setRecurringAmount(0);
            }
        }, 500),
        []
    );

    useEffect(() => {
        getPreview(item);
    }, [
        item.startDate, //
        item.endDate,
        item.frequency,
        item.customFrequency,
        item.unit,
        item.days,
        item.recurEndDate,
        item.recurDayOfMonth,
        item.numberOccurrences,
        item.poNumberParams,
        item.po_number,
    ]);

    const columns = useMemo(() => formatColumns(properties.has24HourClock, localization.general.customer_po), [properties.has24HourClock, localization.general.customer_po]);

    const shouldShowError = item.unit === 'week' || item.unit === 'month' || tooManyItemsError;
    return (
        <RecurringCard title={'Preview of Schedule Items'}>
            {shouldShowError ? (
                <ErrorAlert tooManyItemsError={tooManyItemsError} />
            ) : (
                <>
                    <Alert
                        severity="warning"
                        variant="filled"
                        sx={(theme) => ({
                            marginY: theme.spacing(2),
                        })}
                    >
                        Please review the recurring items before submitting.{' '}
                    </Alert>
                    <Table pageSize={25} columns={columns} rows={preview} />
                </>
            )}
        </RecurringCard>
    );
}

export default PreviewTableCard;
